/**
 * Меню «⋯» (agroai-6al.61): список действий у строки или у шапки.
 * Закрывается кликом вне и Esc (Esc не доходит до панели — она остаётся).
 * Опасное действие просит подтверждения вторым нажатием в том же меню.
 */

import { useEffect, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { Icon } from '../astra/ui/Icon';

export interface MenuItem {
  id: string;
  label: string;
  onSelect: () => void;
  /** Подпись второго нажатия: действие выполняется только после него. */
  confirm?: string;
  danger?: boolean;
  hidden?: boolean;
}

export function KebabMenu({ label, items, disabled = false }: { label: string; items: MenuItem[]; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const [armed, setArmed] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      setArmed(null);
      return;
    }
    const onDown = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopImmediatePropagation();
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  return (
    <div className="agent-menu" ref={box}>
      <button
        type="button"
        className="btn btn--ghost btn--sm focus-ring"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
      >
        <Icon icon={MoreHorizontal} size={16} />
      </button>
      {open ? (
        <div className="agent-menu__list" role="menu" aria-label={label}>
          {items
            .filter((item) => !item.hidden)
            .map((item) => (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                className={`agent-menu__item focus-ring${item.danger ? ' agent-menu__item--danger' : ''}`}
                onClick={() => {
                  if (item.confirm && armed !== item.id) {
                    setArmed(item.id);
                    return;
                  }
                  setOpen(false);
                  item.onSelect();
                }}
              >
                {item.confirm && armed === item.id ? item.confirm : item.label}
              </button>
            ))}
        </div>
      ) : null}
    </div>
  );
}

/** Название, правимое на месте: Enter сохраняет, Esc отменяет. */
export function InlineTitle({
  value,
  onSave,
  onCancel,
  label,
}: {
  value: string;
  onSave: (next: string) => void;
  onCancel: () => void;
  label: string;
}) {
  const [text, setText] = useState(value);
  const ref = useRef<HTMLInputElement>(null);
  const done = useRef(false);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const finish = (save: boolean) => {
    if (done.current) return;
    done.current = true;
    const next = text.trim();
    if (save && next && next !== value) onSave(next);
    else onCancel();
  };
  return (
    <input
      ref={ref}
      className="agent-title-input"
      value={text}
      maxLength={200}
      aria-label={label}
      onChange={(event) => setText(event.target.value)}
      onBlur={() => finish(true)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          finish(true);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          finish(false);
        }
      }}
    />
  );
}
